import { useState, useEffect } from 'react';
import NeurosymbolicVisualizer from './components/NeurosymbolicVisualizer';
import KnowledgeGraph from './components/KnowledgeGraph';
import { getMockDataGenerator } from './data/mockTrainingData';

export default function App() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize the mock data generator
    getMockDataGenerator();
    setTimeout(() => setIsInitialized(true), 500);
  }, []);

  if (!isInitialized) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gray-950">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <div className="text-blue-400 font-mono text-sm">INITIALIZING NEUROSYMBOLIC TRAINING SYSTEM...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen overflow-hidden bg-gray-950 flex flex-col">
      {/* Header HUD */}
      <header className="flex-shrink-0 p-4 border-b border-gray-800">
        <div className="glass-panel p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <span className="text-xl">🦉</span>
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                    NIGHT OWL
                  </h1>
                  <p className="text-xs text-gray-400 font-mono">Neurosymbolic AI Training & Knowledge Extraction</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="text-xs text-gray-400 uppercase">System Status</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm font-mono text-green-400">TRAINING ACTIVE</span>
                </div>
              </div>

              <div className="text-right">
                <div className="text-xs text-gray-400 uppercase">Knowledge Extraction</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                  <span className="text-sm font-mono text-purple-400">PROCESSING</span>
                </div>
              </div>

              <div className="text-right">
                <div className="text-xs text-gray-400 uppercase">Multi-Agent Mode</div>
                <div className="text-sm font-mono text-blue-400 mt-1">12 AGENTS ONLINE</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Norton Commander Style Dual Panel Layout */}
      <main className="flex-1 flex gap-0 overflow-hidden">
        {/* Left Panel - 3D Training Visualization */}
        <div className="w-1/2 h-full border-r border-gray-800 flex flex-col">
          <div className="flex-shrink-0 bg-gray-900 px-4 py-2 border-b border-gray-800">
            <h2 className="text-sm font-mono text-blue-400 uppercase tracking-wider">
              ▸ Multi-Agent Training Visualization
            </h2>
          </div>
          <div className="flex-1 relative">
            <NeurosymbolicVisualizer />
          </div>
        </div>

        {/* Right Panel - Symbolic Knowledge Graphs */}
        <div className="w-1/2 h-full flex flex-col">
          <div className="flex-shrink-0 bg-gray-900 px-4 py-2 border-b border-gray-800">
            <h2 className="text-sm font-mono text-purple-400 uppercase tracking-wider">
              ▸ Symbolic Knowledge Graph Extraction
            </h2>
          </div>
          <div className="flex-1 overflow-hidden">
            <KnowledgeGraph />
          </div>
        </div>
      </main>

      {/* Legend - now smaller and bottom left */}
      <div className="absolute bottom-4 left-4 z-40 glass-panel p-3 w-56">
        <h3 className="text-xs font-bold mb-2 text-gray-300">AGENT TYPES</h3>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 glow-neural" />
            <span className="text-[10px] text-gray-300">Neural Agents</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-purple-500 glow-symbolic" />
            <span className="text-[10px] text-gray-300">Symbolic Agents</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 glow-hybrid" />
            <span className="text-[10px] text-gray-300">Hybrid Agents</span>
          </div>
        </div>
      </div>
    </div>
  );
}
